import {AsyncPipe} from '@angular/common';
import {Component, OnInit, ViewChild} from '@angular/core';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators,} from '@angular/forms';
import {MatButton} from '@angular/material/button';
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {MatOption} from '@angular/material/core';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatSelect} from '@angular/material/select';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {MatTooltip} from '@angular/material/tooltip';
import {ManagedComponent} from '@app/shared/managed.component';
import {SiteConfig, SiteConfigService} from '@app/shared/site-config.service';
import {Stream} from '@app/stream/models';
import {StreamService} from '@app/stream/stream.service';
import {WorkspaceButtonComponent} from '@app/workspace/workspace-button/workspace-button.component';
import {merge, Observable} from 'rxjs';
import {debounceTime, map, take, tap} from 'rxjs/operators';

enum PropertyRequestState {
    Idle,
    Working,
    Success,
    Error,
}

class PropertyRequest {
    property: string;
    state: PropertyRequestState;
    message: string;
}

@Component({
    selector: 'frog-site-card',
    templateUrl: './site-card.component.html',
    styleUrls: ['./site-card.component.scss'],
    imports: [
        MatCard,
        MatCardHeader,
        MatCardTitle,
        MatCardContent,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatProgressSpinner,
        MatIcon,
        MatTooltip,
        WorkspaceButtonComponent,
        MatButton,
        MatSelect,
        MatOption,
        MatSlideToggle,
        AsyncPipe,
    ],
})
export class SiteCardComponent extends ManagedComponent implements OnInit {
    @ViewChild('file', { static: true }) file;

    public form: UntypedFormGroup;
    public thumbnailFile: File;
    public streams$: Observable<Stream[]>;
    public siteConfig$: Observable<SiteConfig>;
    public propertyRequest: PropertyRequest;
    public siteConfig: SiteConfig;

    constructor(
        private siteConfigService: SiteConfigService,
        private streamService: StreamService,
        private fb: UntypedFormBuilder,
    ) {
        super();
        this.form = fb.group({
            name: ['', Validators.required],
            enable_likes: false,
            display_likes: false,
            primary_stream: [null, Validators.required],
        });

        this.propertyRequest = new PropertyRequest();
    }

    ngOnInit(): void {
        this.streams$ = this.streamService
            .listStreams()
            .pipe(map((streams) => streams.filter((stream) => stream.parent === null)));
        this.siteConfig$ = this.siteConfigService.siteConfig.pipe(
            take(1),
            tap((siteConfig) => {
                this.form.patchValue(siteConfig.data);
                this.siteConfig = siteConfig;
            }),
        );

        const observables = Object.entries(this.form.controls).map((value) => {
            return value[1].valueChanges.pipe(
                debounceTime(300),
                map(() => value[0]),
            );
        });
        const sub = merge(...observables).subscribe((key) => this.updateValue(key));
        this.subscriptions.push(sub);
    }

    addFiles() {
        this.file.nativeElement.click();
    }

    onFilesAdded() {
        this.thumbnailFile = this.file.nativeElement.files[0];
        const reader = new FileReader();
        reader.readAsDataURL(this.thumbnailFile);

        this.form.markAsDirty();
        this.updateValue('thumbnail');
    }

    updateValue(key: string) {
        if (this.form.pristine) {
            return;
        }

        this.propertyRequest.property = key;
        this.propertyRequest.state = PropertyRequestState.Working;
        let observable;

        if (key === 'thumbnail') {
            observable = this.siteConfigService.saveThumbnail(this.thumbnailFile);
        } else {
            const data = Object.assign(this.siteConfig.data, this.form.value);
            observable = this.siteConfigService.save(data);
        }

        observable.subscribe(
            () => {
                this.propertyRequest.state = PropertyRequestState.Success;
            },
            (err) => {
                this.propertyRequest.message = err.error.detail;
                this.propertyRequest.state = PropertyRequestState.Error;
            },
        );
    }
}
